import React from 'react';
import DieFace from './DieFace';

interface BiddingInterfaceProps {
  quantity: number;
  faceValue: number; // 0 means no face value selected
  onQuantityChange: (quantity: number) => void;
  onFaceValueChange: (faceValue: number) => void;
  onMakeBid: () => void;
  onChallenge?: () => void;
  canBid: boolean;
  canChallenge: boolean;
}

const BiddingInterface: React.FC<BiddingInterfaceProps> = ({
  quantity,
  faceValue,
  onQuantityChange,
  onFaceValueChange,
  onMakeBid,
  onChallenge,
  canBid,
  canChallenge,
}) => {
  const handleQuantityIncrease = () => {
    onQuantityChange(Math.min(quantity + 1, 30));
  };

  const handleQuantityDecrease = () => {
    onQuantityChange(Math.max(quantity - 1, 1));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-2 space-y-2 flex-shrink-0">
      <h2 className="text-xs font-bold text-gray-900 dark:text-white">Your Turn</h2>

      {/* Quantity and Face Value - Side by Side */}
      <div className="flex flex-row gap-2 justify-center">
        {/* Quantity Selection */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">
            Quantity
          </label>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={handleQuantityDecrease}
              disabled={quantity <= 1}
              className="w-8 h-8 rounded-full bg-primary-500 hover:bg-primary-600 disabled:bg-surface-300 dark:disabled:bg-gray-600 text-white font-bold text-lg shadow active:scale-95 disabled:cursor-not-allowed"
            >
              −
            </button>
            <div className="w-16 h-10 bg-surface-50 dark:bg-gray-700 rounded flex items-center justify-center border border-surface-300 dark:border-gray-600">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">{quantity}</span>
            </div>
            <button
              onClick={handleQuantityIncrease}
              disabled={quantity >= 30}
              className="w-8 h-8 rounded-full bg-primary-500 hover:bg-primary-600 disabled:bg-surface-300 dark:disabled:bg-gray-600 text-white font-bold text-lg shadow active:scale-95 disabled:cursor-not-allowed"
            >
              +
            </button>
          </div>
        </div>

        {/* Face Value Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 text-center">
            Face Value
          </label>
          <div className="grid grid-cols-3 gap-1 w-fit">
            {[1, 2, 3, 4, 5, 6].map((value) => (
              <DieFace
                key={value}
                value={value}
                size="xs"
                variant="control"
                onClick={() => onFaceValueChange(value)}
                selected={faceValue === value}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons - Compact */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onMakeBid}
          disabled={!canBid}
          className="bg-primary-500 hover:bg-primary-600 disabled:bg-surface-300 dark:disabled:bg-gray-600 text-white font-semibold py-2 px-3 text-xs rounded transition-all shadow active:scale-95 disabled:cursor-not-allowed disabled:text-surface-500 dark:disabled:text-gray-400"
        >
          Bid
        </button>
        {canChallenge && onChallenge && (
          <button
            onClick={onChallenge}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-3 text-xs rounded transition-all shadow active:scale-95"
          >
            Challenge!
          </button>
        )}
      </div>

      {!canBid && faceValue > 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 text-center bg-amber-50 dark:bg-amber-900/30 py-1 px-2 rounded">
          'Bid must be higher'
        </p>
      )}
    </div>
  );
};

export default BiddingInterface;
